import React, { useEffect, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";

type CustomShape = {
    type: "circle" | "polygon" | "polyline";
    center?: number[]; // [lng, lat] for circles
    radius?: number; // radius in meters for circles
    geometry?: number[][]; // [[lng, lat], ...] for polygons/polylines
};

type KMLPreviewModalProps = {
    shapes: CustomShape[];
    onClose: () => void;
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "";

const MapContent: React.FC<{ shapes: CustomShape[] }> = ({ shapes }) => {
    const map = useMap();
    const shapesRef = useRef<(google.maps.Circle | google.maps.Polygon | google.maps.Polyline)[]>([]);

    useEffect(() => {
        if (!map) return;

        // Clear existing shapes
        shapesRef.current.forEach(shape => shape.setMap(null));
        shapesRef.current = [];

        const newShapes: (google.maps.Circle | google.maps.Polygon | google.maps.Polyline)[] = [];

        // Add each shape to the map
        shapes.forEach((shapeData) => {
            if (shapeData.type === "circle" && shapeData.center) {
                // shapeData.center is [lng, lat], Google Maps expects {lat, lng}
                const center = { lat: shapeData.center[1], lng: shapeData.center[0] };

                const circle = new google.maps.Circle({
                    center: center,
                    radius: shapeData.radius || 5000,
                    fillColor: "#3b82f6",
                    fillOpacity: 0.2,
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    map: map,
                });

                newShapes.push(circle);
            } else if (shapeData.type === "polygon" && shapeData.geometry) {
                // shapeData.geometry is [[lng, lat], ...], Google Maps expects [{lat, lng}, ...]
                const paths = shapeData.geometry.map(coord => ({ lat: coord[1], lng: coord[0] }));

                const polygon = new google.maps.Polygon({
                    paths: paths,
                    fillColor: "#3b82f6",
                    fillOpacity: 0.2,
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    map: map,
                });

                newShapes.push(polygon);
            } else if (shapeData.type === "polyline" && shapeData.geometry) {
                // shapeData.geometry is [[lng, lat], ...], Google Maps expects [{lat, lng}, ...]
                const paths = shapeData.geometry.map(coord => ({ lat: coord[1], lng: coord[0] }));

                const polyline = new google.maps.Polyline({
                    path: paths,
                    strokeWeight: 3,
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.8,
                    map: map,
                });

                newShapes.push(polyline);
            }
        });

        shapesRef.current = newShapes;

        // Fit bounds to show all shapes
        if (newShapes.length > 0) {
            const bounds = new google.maps.LatLngBounds();

            newShapes.forEach(shape => {
                if (shape instanceof google.maps.Circle) {
                    const center = shape.getCenter();
                    if (center) {
                        bounds.extend(center);
                        // Extend bounds by radius to ensure the whole circle is visible
                        const radius = shape.getRadius();
                        const north = google.maps.geometry.spherical.computeOffset(center, radius, 0);
                        const south = google.maps.geometry.spherical.computeOffset(center, radius, 180);
                        const east = google.maps.geometry.spherical.computeOffset(center, radius, 90);
                        const west = google.maps.geometry.spherical.computeOffset(center, radius, 270);
                        bounds.extend(north);
                        bounds.extend(south);
                        bounds.extend(east);
                        bounds.extend(west);
                    }
                } else if (shape instanceof google.maps.Polygon) {
                    shape.getPath().forEach(latLng => bounds.extend(latLng));
                } else if (shape instanceof google.maps.Polyline) {
                    shape.getPath().forEach(latLng => bounds.extend(latLng));
                }
            });

            map.fitBounds(bounds, 50); // 50px padding
        }

        return () => {
            shapesRef.current.forEach(shape => shape.setMap(null));
        };
    }, [shapes, map]);

    return null;
};

const MapPreview: React.FC<{ shapes: CustomShape[] }> = ({ shapes }) => {
    return (
        <Map
            mapId="kml-preview-map"
            defaultCenter={{ lat: 0, lng: 0 }}
            defaultZoom={2}
            gestureHandling="greedy"
            disableDefaultUI={false}
            style={{ width: "100%", height: "500px" }}
        >
            <MapContent shapes={shapes} />
        </Map>
    );
};

export const KMLPreviewModal: React.FC<KMLPreviewModalProps> = ({ shapes, onClose }) => {
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">KML Preview</h3>
                    <button
                        onClick={onClose}
                        className="text-grey hover:text-dark-grey text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                <div className="mb-4">
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <MapPreview shapes={shapes} />
                    </APIProvider>
                </div>
            </div>
        </div>
    );
};
